/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Token Cache - In-memory token caching and deduplication
 */

import { logger } from "@/lib/utils/logger";
import { TOKEN_CLEANUP_INTERVAL_MS } from "./token-constants";
import { resetActiveRequests } from "./token-queue";
import { resetTokenLoadingState } from "./token-state";
import type { CachedToken } from "./token-types";

// ============================================================================
// TOKEN CACHE STORAGE
// ============================================================================

/**
 * In-memory token cache (per session)
 * Key: session identifier, Value: cached token with expiry
 */
export const tokenCache = new Map<string, CachedToken>();

/**
 * Track in-flight token requests to deduplicate concurrent calls
 */
export const inFlightRequests = new Map<string, Promise<string | null>>();

// ============================================================================
// CLEANUP MANAGEMENT
// ============================================================================

/**
 * Cleanup interval reference
 * Use a single interval per module instance to prevent memory leaks
 */
let cleanupInterval: NodeJS.Timeout | null = null;

/**
 * Initialize token cache cleanup interval (client-side only)
 */
export function initializeCleanupInterval(): void {
  if (typeof window !== "undefined") {
    // Only create one interval, even if module is hot-reloaded
    if (!cleanupInterval) {
      cleanupInterval = setInterval(() => {
        const now = Date.now();
        for (const [key, cached] of tokenCache.entries()) {
          if (cached.expiresAt < now) {
            tokenCache.delete(key);
          }
        }
        // Also clean up stale in-flight requests (shouldn't happen, but safety)
        if (inFlightRequests.size > 100) {
          // If we have too many in-flight requests, something is wrong
          logger.warn(
            "[getSupabaseToken] Too many in-flight requests, clearing cache",
            {
              inFlightRequestsCount: inFlightRequests.size,
              feature: "token-cache",
            },
          );
          inFlightRequests.clear();
        }
      }, TOKEN_CLEANUP_INTERVAL_MS);
    }
  }
}

/**
 * Stop token cache cleanup interval
 * Useful for cleanup in tests or client-side shutdown scenarios
 */
export function stopTokenCacheCleanup(): void {
  if (cleanupInterval) {
    clearInterval(cleanupInterval);
    cleanupInterval = null;
    if (process.env.NODE_ENV === "development") {
      logger.debug("[getSupabaseToken] Token cache cleanup interval stopped");
    }
  }
}

// ============================================================================
// CACHE KEY MANAGEMENT
// ============================================================================

/**
 * Get a cache key for the current session
 * Uses a simple identifier - in a real app, you might want to include user/org ID
 */
export function getCacheKey(): string {
  // Use a simple key since tokens are per-session
  // In browser, we can use a window-scoped identifier
  if (typeof window !== "undefined") {
    if (!window.__ECP_TOKEN_CACHE_KEY__) {
      window.__ECP_TOKEN_CACHE_KEY__ = `token:${Date.now()}`;
    }
    return window.__ECP_TOKEN_CACHE_KEY__;
  }
  return "token:server";
}

/**
 * Reset cache key on next access (forces new cache entry)
 */
function resetCacheKey(): void {
  if (typeof window !== "undefined") {
    delete window.__ECP_TOKEN_CACHE_KEY__;
  }
}

// ============================================================================
// CACHE OPERATIONS
// ============================================================================

/**
 * Clear token cache (useful for logout or session changes)
 */
export function clearTokenCache(): void {
  tokenCache.clear();
  inFlightRequests.clear();
  resetCacheKey();
  resetActiveRequests();
  resetTokenLoadingState();
}

// Initialize cleanup interval on module load (client-side)
initializeCleanupInterval();
